import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { patientApi } from '@services/patient.service'
import type {
  ClinicalHistory,
  ClinicalHistoryCreateRequest,
  ClinicalHistoryAddendumRequest,
  ClinicalHistoryNoteRequest,
} from '@/types'

const CLINICAL_HISTORY_KEY = 'clinical-history'

export function useClinicalHistory(patientId: string, options?: { enabled?: boolean }) {
  return useQuery<ClinicalHistory>({
    queryKey: [CLINICAL_HISTORY_KEY, patientId],
    queryFn: () => patientApi.getClinicalHistory(patientId),
    enabled: options?.enabled ?? !!patientId,
  })
}

function useInvalidateOnSuccess() {
  const queryClient = useQueryClient()
  return (data: ClinicalHistory, patientId: string) => {
    queryClient.setQueryData([CLINICAL_HISTORY_KEY, patientId], data)
    queryClient.invalidateQueries({ queryKey: [CLINICAL_HISTORY_KEY, patientId] })
  }
}

export function useCreateClinicalHistory() {
  const sync = useInvalidateOnSuccess()
  return useMutation<ClinicalHistory, Error, { patientId: string; data?: ClinicalHistoryCreateRequest }>({
    mutationFn: ({ patientId, data = {} }) => patientApi.createClinicalHistory(patientId, data),
    onSuccess: (res, v) => sync(res, v.patientId),
  })
}

export function useUpdateClinicalHistory() {
  const sync = useInvalidateOnSuccess()
  return useMutation<ClinicalHistory, Error, { patientId: string; content: Record<string, unknown> }>({
    mutationFn: ({ patientId, content }) => patientApi.updateClinicalHistory(patientId, content),
    onSuccess: (res, v) => sync(res, v.patientId),
  })
}

export function useSignClinicalHistory() {
  const sync = useInvalidateOnSuccess()
  return useMutation<ClinicalHistory, Error, { patientId: string }>({
    mutationFn: ({ patientId }) => patientApi.signClinicalHistory(patientId),
    onSuccess: (res, v) => sync(res, v.patientId),
  })
}

export function useAddClinicalHistoryAddendum() {
  const sync = useInvalidateOnSuccess()
  return useMutation<ClinicalHistory, Error, { patientId: string; data: ClinicalHistoryAddendumRequest }>({
    mutationFn: ({ patientId, data }) => patientApi.addClinicalHistoryAddendum(patientId, data),
    onSuccess: (res, v) => sync(res, v.patientId),
  })
}

export function useAddClinicalHistoryNote() {
  const sync = useInvalidateOnSuccess()
  return useMutation<ClinicalHistory, Error, { patientId: string; data: ClinicalHistoryNoteRequest }>({
    mutationFn: ({ patientId, data }) => patientApi.addClinicalHistoryNote(patientId, data),
    onSuccess: (res, v) => sync(res, v.patientId),
  })
}
